import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_URL } from '../../../api/api';
import {
    Search,
    Filter,
    Download,
    Eye,
    Edit,
    Trash2,
    MoreVertical,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    RefreshCw,
    Users,
    TrendingUp,
    TrendingDown,
    CheckCircle,
    XCircle,
    AlertCircle,
    Calendar,
    Building,
    Phone,
    Hash
} from 'lucide-react';

const API_GROUP = `${API_URL}/groups`;

const ManageGroupLayer = () => {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const token = localStorage.getItem("token");
    const navigate = useNavigate();

    // Search and Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [dateFilter, setDateFilter] = useState({ from: '', to: '' });

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Stats states
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        inactive: 0,
        totalCashBalance: 0,
        totalProductBalance: 0
    });

    // Sort states
    const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });

    // Fetch groups
    const fetchGroups = useCallback(async () => {
        try {
            const res = await axios.get(API_GROUP, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setGroups(res.data || []);
            calculateStats(res.data || []);
        } catch (error) {
            console.error("Error fetching groups:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch groups',
                timer: 2000,
                showConfirmButton: false
            });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [token]);

    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    // Calculate statistics
    const calculateStats = (data) => {
        const stats = {
            total: data.length,
            active: data.filter(g => g.status?.toLowerCase() === 'active').length,
            inactive: data.filter(g => g.status?.toLowerCase() === 'inactive').length,
            totalCashBalance: data.reduce((sum, g) => sum + (parseFloat(g.cash_balance) || 0), 0),
            totalProductBalance: data.reduce((sum, g) => sum + (parseFloat(g.product_balance) || 0), 0)
        };
        setStats(stats);
    };

    // Filter groups
    const filteredGroups = useMemo(() => {
        return groups.filter(group => {
            const matchesSearch =
                group.groupname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                group.mobile?.includes(searchTerm) ||
                group.city?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus =
                selectedStatus === 'all' ||
                group.status?.toLowerCase() === selectedStatus;

            const matchesDate = () => {
                if (!dateFilter.from && !dateFilter.to) return true;

                const groupDate = new Date(group.enrollmentdate);
                const fromDate = dateFilter.from ? new Date(dateFilter.from) : null;
                const toDate = dateFilter.to ? new Date(dateFilter.to) : null;

                if (fromDate && toDate) {
                    return groupDate >= fromDate && groupDate <= toDate;
                } else if (fromDate) {
                    return groupDate >= fromDate;
                } else if (toDate) {
                    return groupDate <= toDate;
                }
                return true;
            };

            return matchesSearch && matchesStatus && matchesDate();
        });
    }, [groups, searchTerm, selectedStatus, dateFilter]);

    // Sort groups
    const sortedGroups = useMemo(() => {
        const sortableItems = [...filteredGroups];
        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                if (sortConfig.key.includes('.')) {
                    const keys = sortConfig.key.split('.');
                    const aVal = keys.reduce((obj, key) => obj?.[key], a);
                    const bVal = keys.reduce((obj, key) => obj?.[key], b);

                    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                    return 0;
                }

                if (!isNaN(parseFloat(aValue)) && !isNaN(parseFloat(bValue))) {
                    return sortConfig.direction === 'asc'
                        ? parseFloat(aValue) - parseFloat(bValue)
                        : parseFloat(bValue) - parseFloat(aValue);
                }

                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [filteredGroups, sortConfig]);

    // Pagination
    const totalPages = Math.ceil(sortedGroups.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentGroups = sortedGroups.slice(startIndex, endIndex);

    // Handle sort
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Handle refresh
    const handleRefresh = () => {
        setRefreshing(true);
        fetchGroups();
    };

    // Handle export - FIXED TO ACTUALLY EXPORT FILE
    const handleExport = () => {
        const exportData = sortedGroups.map(group => ({
            'Group ID': group.id,
            'Group Name': group.groupname || '',
            'Mobile': group.mobile || '',
            'City': group.city || '',
            'Cash Balance': parseFloat(group.cash_balance || 0).toFixed(2),
            'Product Balance': parseFloat(group.product_balance || 0).toFixed(2),
            'Total Paid (Cash)': parseFloat(group.cash_paid_total || 0).toFixed(2),
            'Total Loan (Cash)': parseFloat(group.cash_loan_total || 0).toFixed(2),
            'Total Paid (Product)': parseFloat(group.product_paid_total || 0).toFixed(2),
            'Total Loan (Product)': parseFloat(group.product_loan_total || 0).toFixed(2),
            'Enrollment Date': group.enrollmentdate || '',
            'Collection Day': group.collectionday || '',
            'Status': group.status || '',
            'Address': group.address || '',
            'Created At': group.created_at || ''
        }));

        // Create CSV content
        const headers = Object.keys(exportData[0] || {}).join(',');
        const rows = exportData.map(item =>
            Object.values(item).map(value =>
                typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value
            ).join(',')
        );
        const csvContent = [headers, ...rows].join('\n');

        // Create Excel (CSV) file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // Create filename with timestamp
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
        link.setAttribute('download', `groups-export-${timestamp}.csv`);

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        Swal.fire({
            icon: 'success',
            title: 'Export Complete!',
            text: `Exported ${exportData.length} groups to CSV file`,
            timer: 2000,
            showConfirmButton: false
        });
    };

    // Handle export with multiple formats
    const handleExportWithFormat = (format = 'csv') => {
        Swal.fire({
            title: 'Export Options',
            html: `
                <div style="text-align: left; padding: 10px;">
                    <p><strong>Select Export Format:</strong></p>
                    <div style="margin-top: 10px;">
                        <label style="display: block; margin-bottom: 8px;">
                            <input type="radio" name="exportFormat" value="csv" checked style="margin-right: 8px;">
                            CSV (Excel) - Recommended
                        </label>
                        <label style="display: block; margin-bottom: 8px;">
                            <input type="radio" name="exportFormat" value="json" style="margin-right: 8px;">
                            JSON (Raw Data)
                        </label>
                        <label style="display: block; margin-bottom: 8px;">
                            <input type="radio" name="exportFormat" value="pdf" style="margin-right: 8px;">
                            PDF Report
                        </label>
                    </div>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Export',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#6b7280',
            preConfirm: () => {
                const format = document.querySelector('input[name="exportFormat"]:checked')?.value;
                return { format };
            }
        }).then((result) => {
            if (result.isConfirmed && result.value) {
                const { format } = result.value;

                if (format === 'csv') {
                    handleExport(); // Call the CSV export function
                } else if (format === 'json') {
                    handleExportJSON();
                } else if (format === 'pdf') {
                    handleExportPDF();
                }
            }
        });
    };

    // Export as JSON
    const handleExportJSON = () => {
        const exportData = sortedGroups.map(group => ({
            id: group.id,
            groupname: group.groupname || '',
            mobile: group.mobile || '',
            city: group.city || '',
            cash_balance: parseFloat(group.cash_balance || 0),
            product_balance: parseFloat(group.product_balance || 0),
            cash_paid_total: parseFloat(group.cash_paid_total || 0),
            cash_loan_total: parseFloat(group.cash_loan_total || 0),
            product_paid_total: parseFloat(group.product_paid_total || 0),
            product_loan_total: parseFloat(group.product_loan_total || 0),
            enrollmentdate: group.enrollmentdate || '',
            collectionday: group.collectionday || '',
            status: group.status || '',
            address: group.address || '',
            created_at: group.created_at || ''
        }));

        const jsonString = JSON.stringify(exportData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
        link.setAttribute('download', `groups-export-${timestamp}.json`);

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        Swal.fire({
            icon: 'success',
            title: 'Export Complete!',
            text: `Exported ${exportData.length} groups to JSON file`,
            timer: 2000,
            showConfirmButton: false
        });
    };

    // Export as PDF (basic version)
    const handleExportPDF = () => {
        // For PDF export, you can use libraries like jsPDF or html2pdf
        // Here's a basic implementation
        Swal.fire({
            icon: 'info',
            title: 'PDF Export',
            text: 'PDF export functionality requires additional setup. For now, please use CSV or JSON export.',
            confirmButtonText: 'OK',
            confirmButtonColor: '#3b82f6'
        });
    };

    // Handle delete
    const handleDelete = (groupId, groupName) => {
        Swal.fire({
            title: 'Delete Group',
            html: `Are you sure you want to delete <strong>${groupName}</strong>?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            reverseButtons: true
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`${API_GROUP}/${groupId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Group has been deleted.',
                        timer: 1500,
                        showConfirmButton: false
                    });
                    fetchGroups();
                } catch (error) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Failed to delete group',
                        timer: 2000,
                        showConfirmButton: false
                    });
                }
            }
        });
    };

    // Handle status toggle
    const handleStatusToggle = async (groupId, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

        try {
            await axios.put(`${API_GROUP}/${groupId}/status`, {
                status: newStatus
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            Swal.fire({
                icon: 'success',
                title: 'Updated!',
                text: `Group status updated to ${newStatus}`,
                timer: 1500,
                showConfirmButton: false
            });

            fetchGroups();
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to update status',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };

    // Loading skeleton
    if (loading) {
        return (
            <div style={containerStyle}>
                <div style={innerContainerStyle}>
                    <div style={skeletonContainer}>
                        <div style={skeletonTitle}></div>
                        <div style={statsGridStyle}>
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} style={skeletonCard}></div>
                            ))}
                        </div>
                        <div style={skeletonTable}>
                            <div style={skeletonHeader}></div>
                            <div style={skeletonRows}>
                                {[1, 2, 3, 4, 5].map(i => (
                                    <div key={i} style={skeletonRow}></div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={containerStyle}>
            <div style={innerContainerStyle}>
                {/* Header */}
                <div style={headerStyle}>
                    <div>
                        <h4 style={titleStyle}>Group Management</h4>
                        <p style={subtitleStyle}>Manage and monitor all groups</p>
                    </div>

                    <div style={headerActionsStyle}>
                        <button
                            onClick={handleRefresh}
                            disabled={refreshing}
                            style={refreshing ? refreshButtonDisabledStyle : refreshButtonStyle}
                            className="refresh-button"
                        >
                            <RefreshCw size={16} style={refreshing ? { animation: 'spin 1s linear infinite' } : {}} />
                            {refreshing ? 'Refreshing...' : 'Refresh'}
                        </button>

                        <button
                            onClick={() => handleExportWithFormat()}
                            style={exportButtonStyle}
                            className="export-button"
                        >
                            <Download size={16} />
                            Export
                        </button>

                        <Link
                            to="/add-groups"
                            style={addButtonStyle}
                            className="add-button"
                        >
                            <Users size={16} />
                            Add Group
                        </Link>
                    </div>
                </div>

                {/* Stats Cards */}
                <div style={statsGridStyle}>
                    <div style={statCardBlueStyle} className="stat-card">
                        <div style={statCardContentStyle}>
                            <div>
                                <p style={statLabelStyle}>Total Groups</p>
                                <p style={statValueBlueStyle}>{stats.total}</p>
                            </div>
                            <div style={statIconBlueStyle}>
                                <Users size={24} />
                            </div>
                        </div>
                    </div>

                    <div style={statCardGreenStyle} className="stat-card">
                        <div style={statCardContentStyle}>
                            <div>
                                <p style={statLabelStyle}>Active Groups</p>
                                <p style={statValueGreenStyle}>{stats.active}</p>
                            </div>
                            <div style={statIconGreenStyle}>
                                <CheckCircle size={24} />
                            </div>
                        </div>
                    </div>

                    <div style={statCardPurpleStyle} className="stat-card">
                        <div style={statCardContentStyle}>
                            <div>
                                <p style={statLabelStyle}>Cash Balance</p>
                                <p style={statValuePurpleStyle}>
                                    ₹{stats.totalCashBalance.toLocaleString()}
                                </p>
                            </div>
                            <div style={statIconPurpleStyle}>
                                <TrendingUp size={24} />
                            </div>
                        </div>
                    </div>

                    <div style={statCardAmberStyle} className="stat-card">
                        <div style={statCardContentStyle}>
                            <div>
                                <p style={statLabelStyle}>Product Balance</p>
                                <p style={statValueAmberStyle}>
                                    ₹{stats.totalProductBalance.toLocaleString()}
                                </p>
                            </div>
                            <div style={statIconAmberStyle}>
                                <TrendingDown size={24} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters Card */}
                <div style={filtersCardStyle}>
                    <div style={filtersCardHeaderStyle}>
                        <h5 style={filtersTitleStyle}>Filters & Search</h5>
                    </div>

                    <div style={filtersContentStyle}>
                        <div style={filtersGridStyle}>
                            {/* Search */}
                            <div>
                                <label style={filterLabelStyle}>Search Groups</label>
                                <div style={searchContainerStyle}>
                                    <Search style={searchIconStyle} size={18} />
                                    <input
                                        type="text"
                                        placeholder="Search by name, mobile, or city..."
                                        style={searchInputStyle}
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className="search-input"
                                    />
                                </div>
                            </div>

                            {/* Status Filter */}
                            <div>
                                <label style={filterLabelStyle}>Status</label>
                                <select
                                    style={selectInputStyle}
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                    className="select-input"
                                >
                                    <option value="all">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="pending">Pending</option>
                                </select>
                            </div>

                            {/* Date Range */}
                            <div>
                                <label style={filterLabelStyle}>Enrollment Date</label>
                                <div style={dateGridStyle}>
                                    <input
                                        type="date"
                                        style={dateInputStyle}
                                        value={dateFilter.from}
                                        onChange={(e) => setDateFilter(prev => ({ ...prev, from: e.target.value }))}
                                        className="date-input"
                                    />
                                    <input
                                        type="date"
                                        style={dateInputStyle}
                                        value={dateFilter.to}
                                        onChange={(e) => setDateFilter(prev => ({ ...prev, to: e.target.value }))}
                                        className="date-input"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Active filters display */}
                        {(searchTerm || selectedStatus !== 'all' || dateFilter.from || dateFilter.to) && (
                            <div style={activeFiltersStyle}>
                                <span style={activeFiltersLabelStyle}>Active filters:</span>
                                {searchTerm && (
                                    <span style={filterTagBlueStyle} className="filter-tag">
                                        Search: {searchTerm}
                                        <button
                                            onClick={() => setSearchTerm('')}
                                            style={filterTagCloseStyle}
                                            className="filter-tag-close"
                                        >
                                            &times;
                                        </button>
                                    </span>
                                )}
                                {selectedStatus !== 'all' && (
                                    <span style={filterTagGreenStyle} className="filter-tag">
                                        Status: {selectedStatus}
                                        <button
                                            onClick={() => setSelectedStatus('all')}
                                            style={filterTagCloseStyle}
                                            className="filter-tag-close"
                                        >
                                            &times;
                                        </button>
                                    </span>
                                )}
                                {(dateFilter.from || dateFilter.to) && (
                                    <span style={filterTagPurpleStyle} className="filter-tag">
                                        Date: {dateFilter.from || 'Start'} - {dateFilter.to || 'End'}
                                        <button
                                            onClick={() => setDateFilter({ from: '', to: '' })}
                                            style={filterTagCloseStyle}
                                            className="filter-tag-close"
                                        >
                                            &times;
                                        </button>
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Table Card */}
                <div style={tableCardStyle}>
                    <div style={tableHeaderStyle}>
                        <div>
                            <h5 style={tableTitleStyle}>Groups List</h5>
                            <p style={tableSubtitleStyle}>
                                Showing {currentGroups.length} of {sortedGroups.length} groups
                            </p>
                        </div>

                        <div style={tableControlsStyle}>
                            <div style={itemsPerPageStyle}>
                                <span style={itemsPerPageLabelStyle}>Show:</span>
                                <select
                                    style={itemsPerPageSelectStyle}
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value="5">5</option>
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                </select>
                                <span style={itemsPerPageLabelStyle}>per page</span>
                            </div>
                        </div>
                    </div>

                    <div style={tableWrapperStyle} className="table-wrapper">
                        <table style={tableStyle}>
                            <thead style={tableHeadStyle}>
                                <tr>
                                    {[
                                        { key: 'groupname', label: 'Group Name' },
                                        { key: 'mobile', label: 'Mobile' },
                                        { key: 'city', label: 'City' },
                                        { key: 'cash_balance', label: 'Cash Balance' },
                                        { key: 'product_balance', label: 'Product Balance' },
                                        { key: 'enrollmentdate', label: 'Enrollment' },
                                        { key: 'collectionday', label: 'C. Day' },
                                        { key: 'status', label: 'Status' },
                                        { key: 'actions', label: 'Actions' }
                                    ].map(({ key, label }) => (
                                        <th
                                            key={key}
                                            style={key !== 'actions' ? sortableHeaderCellStyle : headerCellStyle}
                                            onClick={() => key !== 'actions' && handleSort(key)}
                                            className={key !== 'actions' ? 'sortable-header-cell' : ''}
                                        >
                                            <div style={headerCellContentStyle}>
                                                {label}
                                                {key !== 'actions' && sortConfig.key === key && (
                                                    <span style={sortIndicatorStyle}>
                                                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody style={tableBodyStyle}>
                                {currentGroups.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" style={emptyStateStyle}>
                                            <div style={emptyStateContentStyle}>
                                                <Users style={emptyStateIconStyle} size={48} />
                                                <h3 style={emptyStateTitleStyle}>No groups found</h3>
                                                <p style={emptyStateTextStyle}>
                                                    {searchTerm || selectedStatus !== 'all' || dateFilter.from || dateFilter.to
                                                        ? 'Try adjusting your filters to find what you\'re looking for.'
                                                        : 'Get started by adding your first group.'}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    currentGroups.map((group, index) => (
                                        <tr key={group.id} style={tableRowStyle} className="table-row">
                                            <td style={tableCellStyle}>
                                                <div>
                                                    <div style={groupNameStyle}>{group.groupname}</div>
                                                    <div style={groupIdStyle}>ID: {group.id}</div>
                                                </div>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <a
                                                    href={`tel:${group.mobile}`}
                                                    style={phoneLinkStyle}
                                                    className="phone-link"
                                                >
                                                    <Phone size={14} style={{ marginRight: '4px' }} />
                                                    {group.mobile}
                                                </a>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <div style={cityCellStyle}>
                                                    <Building size={14} style={{ marginRight: '4px' }} />
                                                    <span>{group.city || 'N/A'}</span>
                                                </div>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <div style={balanceCellStyle}>
                                                    <div style={balanceAmountStyle}>
                                                        ₹{parseFloat(group.cash_balance || 0).toLocaleString()}
                                                    </div>
                                                    <div style={balanceDetailsStyle}>
                                                        Paid: ₹{parseFloat(group.cash_paid_total || 0).toLocaleString()} |
                                                        Loan: ₹{parseFloat(group.cash_loan_total || 0).toLocaleString()}
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <div style={balanceCellStyle}>
                                                    <div style={balanceAmountStyle}>
                                                        ₹{parseFloat(group.product_balance || 0).toLocaleString()}
                                                    </div>
                                                    <div style={balanceDetailsStyle}>
                                                        Paid: ₹{parseFloat(group.product_paid_total || 0).toLocaleString()} |
                                                        Loan: ₹{parseFloat(group.product_loan_total || 0).toLocaleString()}
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <div style={dateCellStyle}>
                                                    <Calendar size={14} style={{ marginRight: '4px' }} />
                                                    <div>
                                                        <div style={dateValueStyle}>{group.enrollmentdate}</div>
                                                        <div style={dateAgoStyle}>
                                                            {Math.floor((new Date() - new Date(group.enrollmentdate)) / (1000 * 60 * 60 * 24))} days ago
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <span style={collectionDayBadgeStyle}>
                                                    <Hash size={14} style={{ marginRight: '2px' }} />
                                                    {group.collectionday}
                                                </span>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <button
                                                    onClick={() => handleStatusToggle(group.id, group.status)}
                                                    style={
                                                        group.status?.toLowerCase() === 'active'
                                                            ? statusButtonActiveStyle
                                                            : statusButtonInactiveStyle
                                                    }
                                                    className="status-button"
                                                >
                                                    {group.status?.toLowerCase() === 'active' ? (
                                                        <>
                                                            <CheckCircle size={14} />
                                                            Active
                                                        </>
                                                    ) : (
                                                        <>
                                                            <XCircle size={14} />
                                                            Inactive
                                                        </>
                                                    )}
                                                </button>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <div style={actionButtonsStyle}>
                                                    {/* <Link
                                                        to={`/group/${group.id}/view`}
                                                        style={actionButtonViewStyle}
                                                        title="View Details"
                                                        className="action-button-view"
                                                    >
                                                        <Eye size={18} />
                                                    </Link> */}

                                                    <Link
                                                        to={`/add-groups/${group.id}`}
                                                        style={actionButtonEditStyle}
                                                        title="Edit"
                                                        className="action-button-edit"
                                                    >
                                                        <Edit size={18} />
                                                    </Link>

                                                    <button
                                                        onClick={() => handleDelete(group.id, group.groupname)}
                                                        style={actionButtonDeleteStyle}
                                                        title="Delete"
                                                        className="action-button-delete"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>

                                                    {/* <button
                                                        style={actionButtonMoreStyle}
                                                        title="More options"
                                                        className="action-button-more"
                                                    >
                                                        <MoreVertical size={18} />
                                                    </button> */}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {sortedGroups.length > 0 && (
                        <div style={paginationContainerStyle} className="pagination-container">
                            <div style={paginationInfoStyle}>
                                Showing <span style={paginationInfoBoldStyle}>{startIndex + 1}</span> to{' '}
                                <span style={paginationInfoBoldStyle}>{Math.min(endIndex, sortedGroups.length)}</span> of{' '}
                                <span style={paginationInfoBoldStyle}>{sortedGroups.length}</span> results
                            </div>

                            <div style={paginationControlsStyle} className="pagination-controls">
                                <button
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                    style={currentPage === 1 ? paginationButtonDisabledStyle : paginationButtonStyle}
                                    className="pagination-button"
                                >
                                    <ChevronsLeft size={16} />
                                </button>

                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    style={currentPage === 1 ? paginationButtonDisabledStyle : paginationButtonStyle}
                                    className="pagination-button"
                                >
                                    <ChevronLeft size={16} />
                                </button>

                                <div style={paginationNumbersStyle}>
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                style={
                                                    currentPage === pageNum
                                                        ? paginationNumberActiveStyle
                                                        : paginationNumberStyle
                                                }
                                                className="pagination-number"
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}

                                    {totalPages > 5 && currentPage < totalPages - 2 && (
                                        <>
                                            <span style={paginationEllipsisStyle}>...</span>
                                            <button
                                                onClick={() => setCurrentPage(totalPages)}
                                                style={
                                                    currentPage === totalPages
                                                        ? paginationNumberActiveStyle
                                                        : paginationNumberStyle
                                                }
                                                className="pagination-number"
                                            >
                                                {totalPages}
                                            </button>
                                        </>
                                    )}
                                </div>

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    style={currentPage === totalPages ? paginationButtonDisabledStyle : paginationButtonStyle}
                                    className="pagination-button"
                                >
                                    <ChevronRight size={16} />
                                </button>

                                <button
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                    style={currentPage === totalPages ? paginationButtonDisabledStyle : paginationButtonStyle}
                                    className="pagination-button"
                                >
                                    <ChevronsRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <style jsx="true">{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .refresh-button:hover {
                    background-color: #f9fafb !important;
                    border-color: #9ca3af !important;
                    transform: translateY(-1px) !important;
                }
                
                .export-button:hover {
                    background-color: #059669 !important;
                    transform: translateY(-1px) !important;
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2) !important;
                }
                
                .add-button:hover {
                    background-color: #2563eb !important;
                    transform: translateY(-1px) !important;
                    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2) !important;
                }
                
                .stat-card:hover {
                    transform: translateY(-2px) !important;
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
                }
                
                .search-input:focus {
                    border-color: #3b82f6 !important;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
                }
                
                .select-input:focus {
                    border-color: #3b82f6 !important;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
                }
                
                .date-input:focus {
                    border-color: #3b82f6 !important;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
                }
                
                .filter-tag:hover {
                    transform: translateY(-1px) !important;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
                }
                
                .filter-tag-close:hover {
                    opacity: 1 !important;
                }
                
                .sortable-header-cell:hover {
                    background-color: #f3f4f6 !important;
                }
                
                .table-row:hover {
                    background-color: #f9fafb !important;
                }
                
                .phone-link:hover {
                    color: #2563eb !important;
                    text-decoration: underline !important;
                }
                
                .status-button:hover {
                    transform: scale(1.05) !important;
                }
                
                .action-button-view:hover {
                    background-color: #dbeafe !important;
                    color: #1d4ed8 !important;
                    border-color: #bfdbfe !important;
                }
                
                .action-button-edit:hover {
                    background-color: #dcfce7 !important;
                    color: #15803d !important;
                    border-color: #bbf7d0 !important;
                }
                
                .action-button-delete:hover {
                    background-color: #fee2e2 !important;
                    color: #dc2626 !important;
                    border-color: #fecaca !important;
                }
                
                .action-button-more:hover {
                    background-color: #f3f4f6 !important;
                    color: #374151 !important;
                    border-color: #e5e7eb !important;
                }
                
                .pagination-button:hover:not(:disabled) {
                    background-color: #f9fafb !important;
                    border-color: #9ca3af !important;
                }
                
                .pagination-number:hover:not(.active) {
                    background-color: #f3f4f6 !important;
                    border-color: #9ca3af !important;
                }
                
                @media (max-width: 768px) {
                    .container {
                        padding: 12px !important;
                    }
                    
                    .stats-grid {
                        grid-template-columns: repeat(2, 1fr) !important;
                    }
                    
                    .filters-grid {
                        grid-template-columns: 1fr !important;
                    }
                    
                    .header-actions {
                        flex-direction: column !important;
                        width: 100% !important;
                        gap: 8px !important;
                    }
                    
                    .header-actions button, 
                    .header-actions a {
                        width: 100% !important;
                        justify-content: center !important;
                    }
                    
                    .table-header {
                        flex-direction: column !important;
                        gap: 12px !important;
                        align-items: flex-start !important;
                    }
                    
                    .pagination-container {
                        flex-direction: column !important;
                        gap: 12px !important;
                        align-items: flex-start !important;
                    }
                    
                    .pagination-controls {
                        flex-wrap: wrap !important;
                    }
                    
                    .action-buttons {
                        flex-wrap: wrap !important;
                        gap: 4px !important;
                    }
                }
                
                @media (max-width: 480px) {
                    .stats-grid {
                        grid-template-columns: 1fr !important;
                    }
                    
                    .table-wrapper {
                        overflow-x: auto !important;
                        -webkit-overflow-scrolling: touch !important;
                    }
                    
                    .table {
                        min-width: 800px !important;
                    }
                }
            `}</style>
        </div>
    );
};

// Define all styles as separate objects
const containerStyle = {
    minHeight: '100vh',
    backgroundColor: '#f9fafb',
    padding: '16px',
    fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif`,
    animation: 'fadeIn 0.3s ease-in'
};

const innerContainerStyle = {
    maxWidth: '1200px',
    margin: '0 auto'
};

const headerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    marginBottom: '24px',
    gap: '16px'
};

const titleStyle = {
    fontSize: '24px !important',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '4px',
    margin: '0'
};

const subtitleStyle = {
    color: '#6b7280',
    margin: '0',
    fontSize: '14px !important'
};

const headerActionsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap'
};

const refreshButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: 'white',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    color: '#374151',
    fontWeight: '500',
    fontSize: '14px !important',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none'
};

const refreshButtonDisabledStyle = {
    ...refreshButtonStyle,
    opacity: '0.5',
    cursor: 'not-allowed'
};

const exportButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: '#10b981',
    border: '1px solid #10b981',
    borderRadius: '6px',
    color: 'white',
    fontWeight: '500',
    fontSize: '14px !important',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    outline: 'none'
};

const addButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: '#3b82f6',
    border: '1px solid #3b82f6',
    borderRadius: '6px',
    color: 'white',
    fontWeight: '500',
    fontSize: '14px !important',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    outline: 'none'
};

const statsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '24px'
};

const statCardBaseStyle = {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '20px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease'
};

const statCardBlueStyle = {
    ...statCardBaseStyle,
    background: 'linear-gradient(135deg, #eff6ff, #dbeafe)'
};

const statCardGreenStyle = {
    ...statCardBaseStyle,
    background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)'
};

const statCardPurpleStyle = {
    ...statCardBaseStyle,
    background: 'linear-gradient(135deg, #faf5ff, #f3e8ff)'
};

const statCardAmberStyle = {
    ...statCardBaseStyle,
    background: 'linear-gradient(135deg, #fffbeb, #fef3c7)'
};

const statCardContentStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
};

const statLabelStyle = {
    fontSize: '14px !important',
    fontWeight: '500',
    color: '#6b7280',
    marginBottom: '4px',
    margin: '0'
};

const statValueBaseStyle = {
    fontSize: '24px !important',
    fontWeight: '700',
    margin: '0'
};

const statValueBlueStyle = {
    ...statValueBaseStyle,
    color: '#1e40af'
};

const statValueGreenStyle = {
    ...statValueBaseStyle,
    color: '#065f46'
};

const statValuePurpleStyle = {
    ...statValueBaseStyle,
    color: '#7c3aed'
};

const statValueAmberStyle = {
    ...statValueBaseStyle,
    color: '#d97706'
};

const statIconBaseStyle = {
    padding: '12px',
    borderRadius: '8px'
};

const statIconBlueStyle = {
    ...statIconBaseStyle,
    backgroundColor: '#dbeafe',
    color: '#1e40af'
};

const statIconGreenStyle = {
    ...statIconBaseStyle,
    backgroundColor: '#d1fae5',
    color: '#065f46'
};

const statIconPurpleStyle = {
    ...statIconBaseStyle,
    backgroundColor: '#f3e8ff',
    color: '#7c3aed'
};

const statIconAmberStyle = {
    ...statIconBaseStyle,
    backgroundColor: '#fef3c7',
    color: '#d97706'
};

const filtersCardStyle = {
    backgroundColor: 'white',
    borderRadius: '8px',
    marginBottom: '24px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
};

const filtersCardHeaderStyle = {
    padding: '16px',
    borderBottom: '1px solid #e5e7eb'
};

const filtersTitleStyle = {
    fontSize: '13px !important',
    fontWeight: '600 !important',
    color: '#374151 !important',
    margin: '0 !important'
};

const filtersContentStyle = {
    padding: '16px'
};

const filtersGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px'
};

const filterLabelStyle = {
    display: 'block !important',
    fontSize: '14px !important',
    fontWeight: '500 !important',
    color: '#374151 !important',
    marginBottom: '8px !important'
};

const searchContainerStyle = {
    position: 'relative'
};

const searchIconStyle = {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#9ca3af',
    pointerEvents: 'none'
};

const searchInputStyle = {
    width: '100%',
    paddingLeft: '40px',
    paddingRight: '16px',
    paddingTop: '8px',
    paddingBottom: '8px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px !important',
    transition: 'all 0.2s ease',
    outline: 'none'
};

const selectInputStyle = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px !important',
    backgroundColor: 'white',
    color: '#374151',
    outline: 'none',
    cursor: 'pointer'
};

const dateGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '8px'
};

const dateInputStyle = {
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px !important',
    outline: 'none'
};

const activeFiltersStyle = {
    marginTop: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
};

const activeFiltersLabelStyle = {
    fontSize: '14px !important',
    color: '#6b7280 !important'
};

const filterTagBaseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '12px !important',
    fontWeight: '500 !important',
    transition: 'all 0.2s ease'
};

const filterTagBlueStyle = {
    ...filterTagBaseStyle,
    backgroundColor: '#dbeafe',
    color: '#1e40af'
};

const filterTagGreenStyle = {
    ...filterTagBaseStyle,
    backgroundColor: '#d1fae5',
    color: '#065f46'
};

const filterTagPurpleStyle = {
    ...filterTagBaseStyle,
    backgroundColor: '#f3e8ff',
    color: '#7c3aed'
};

const filterTagCloseStyle = {
    background: 'none',
    border: 'none',
    color: 'inherit',
    cursor: 'pointer',
    fontSize: '16px !important',
    lineHeight: '1',
    padding: '0',
    marginLeft: '4px',
    opacity: '0.7',
    transition: 'opacity 0.2s ease',
    outline: 'none'
};

const tableCardStyle = {
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden'
};

const tableHeaderStyle = {
    padding: '16px 24px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '16px'
};

const tableTitleStyle = {
    fontSize: '16px !important',
    fontWeight: '600 !important',
    color: '#374151 !important',
    margin: '0 !important'
};

const tableSubtitleStyle = {
    fontSize: '14px !important',
    color: '#6b7280 !important',
    marginTop: '4px !important',
    margin: '0 !important'
};

const tableControlsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
};

const itemsPerPageStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
};

const itemsPerPageLabelStyle = {
    fontSize: '14px !important',
    color: '#6b7280 !important'
};

const itemsPerPageSelectStyle = {
    padding: '4px 8px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '14px !important',
    outline: 'none',
    cursor: 'pointer'
};

const tableWrapperStyle = {
    overflowX: 'auto'
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse'
};

const tableHeadStyle = {
    backgroundColor: '#f9fafb'
};

const headerCellStyle = {
    padding: '12px 24px',
    textAlign: 'left',
    fontSize: '12px !important',
    fontWeight: '600 !important',
    color: '#6b7280 !important',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #e5e7eb'
};

const sortableHeaderCellStyle = {
    ...headerCellStyle,
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color 0.2s ease'
};

const headerCellContentStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
};

const sortIndicatorStyle = {
    color: '#9ca3af !important',
    fontSize: '10px !important'
};

const tableBodyStyle = {
    backgroundColor: 'white'
};

const tableRowStyle = {
    borderBottom: '1px solid #e5e7eb',
    transition: 'background-color 0.2s ease'
};

const tableCellStyle = {
    padding: '16px 24px',
    fontSize: '14px !important',
    color: '#374151 !important',
    verticalAlign: 'middle'
};

const groupNameStyle = {
    fontWeight: '600',
    color: '#111827',
    marginBottom: '4px'
};

const groupIdStyle = {
    fontSize: '12px !important',
    color: '#6b7280 !important'
};

const phoneLinkStyle = {
    color: '#3b82f6 !important',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    transition: 'color 0.2s ease'
};

const cityCellStyle = {
    display: 'flex',
    alignItems: 'center'
};

const balanceCellStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
};

const balanceAmountStyle = {
    fontWeight: '600',
    color: '#111827'
};

const balanceDetailsStyle = {
    fontSize: '12px !important',
    color: '#6b7280 !important'
};

const dateCellStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
};

const dateValueStyle = {
    fontWeight: '500',
    color: '#111827'
};

const dateAgoStyle = {
    fontSize: '12px !important',
    color: '#6b7280 !important',
    marginTop: '2px !important'
};

const collectionDayBadgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 12px',
    backgroundColor: '#dbeafe !important',
    color: '#1e40af !important',
    borderRadius: '9999px !important',
    fontSize: '12px !important',
    fontWeight: '600 !important'
};

const statusButtonBaseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 12px',
    borderRadius: '9999px !important',
    fontSize: '12px !important',
    fontWeight: '500 !important',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none',
    border: '1px solid'
};

const statusButtonActiveStyle = {
    ...statusButtonBaseStyle,
    backgroundColor: '#d1fae5',
    color: '#065f46',
    borderColor: '#10b981'
};

const statusButtonInactiveStyle = {
    ...statusButtonBaseStyle,
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    borderColor: '#ef4444'
};

const actionButtonsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
};

const actionButtonBaseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px',
    backgroundColor: 'transparent',
    border: '1px solid transparent',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none'
};

const actionButtonViewStyle = {
    ...actionButtonBaseStyle,
    color: '#6b7280',
    textDecoration: 'none'
};

const actionButtonEditStyle = {
    ...actionButtonBaseStyle,
    color: '#6b7280',
    textDecoration: 'none'
};

const actionButtonDeleteStyle = {
    ...actionButtonBaseStyle,
    color: '#6b7280'
};

const actionButtonMoreStyle = {
    ...actionButtonBaseStyle,
    color: '#6b7280'
};

const emptyStateStyle = {
    padding: '48px 24px',
    textAlign: 'center'
};

const emptyStateContentStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px'
};

const emptyStateIconStyle = {
    color: '#9ca3af',
    marginBottom: '16px'
};

const emptyStateTitleStyle = {
    fontSize: '18px !important',
    fontWeight: '600 !important',
    color: '#111827 !important',
    margin: '0 !important'
};

const emptyStateTextStyle = {
    color: '#6b7280',
    maxWidth: '400px',
    margin: '0 auto',
    lineHeight: '1.5'
};

const paginationContainerStyle = {
    padding: '16px 24px',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '16px'
};

const paginationInfoStyle = {
    fontSize: '14px !important',
    color: '#6b7280 !important'
};

const paginationInfoBoldStyle = {
    fontWeight: '600 !important',
    color: '#374151'
};

const paginationControlsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
};

const paginationButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    backgroundColor: 'white',
    color: '#374151',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none'
};

const paginationButtonDisabledStyle = {
    ...paginationButtonStyle,
    cursor: 'not-allowed',
    opacity: '0.5'
};

const paginationNumbersStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
};

const paginationNumberStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    backgroundColor: 'white',
    color: '#374151',
    fontSize: '14px !important',
    fontWeight: '500 !important',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none'
};

const paginationNumberActiveStyle = {
    ...paginationNumberStyle,
    backgroundColor: '#3b82f6',
    color: 'white',
    borderColor: '#3b82f6'
};

const paginationEllipsisStyle = {
    padding: '0 8px',
    color: '#6b7280'
};

// Loading skeleton styles
const skeletonContainer = {
    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
};

const skeletonTitle = {
    height: '32px',
    backgroundColor: '#e5e7eb',
    borderRadius: '4px',
    width: '200px',
    marginBottom: '24px'
};

const skeletonCard = {
    height: '96px',
    backgroundColor: '#e5e7eb',
    borderRadius: '8px'
};

const skeletonTable = {
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    marginTop: '24px'
};

const skeletonHeader = {
    height: '48px',
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    borderRadius: '8px 8px 0 0'
};

const skeletonRows = {
    padding: '16px'
};

const skeletonRow = {
    height: '64px',
    backgroundColor: '#e5e7eb',
    borderRadius: '4px',
    marginBottom: '8px'
};

export default ManageGroupLayer;